import { put } from 'redux-saga/effects';

import { store } from '../store';
import { send } from '../../helpers/requests';
import toastr from '../../helpers/toastr';
import * as actions from './actions';
import AnalyticsClient from '../../analytics/analyticsClient';
import trackEvents from '../../analytics/trackEvents';
import { selectToken } from '../selectors';

export function* activateMfaRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/users/activate2fa',
      method: 'POST',
      token: selectToken(store.getState()),
      data: payload.data,
    });
    payload.data.cb && payload.data.cb();
    yield put(actions.activateMfaRequestSuccess(res));
  } catch (error) {
    yield put(actions.activateMfaRequestFailure(error));
  }
}

export function* deactivateMfaRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/users/deactivate2fa',
      method: 'POST',
      token: selectToken(store.getState()),
      data: payload.data,
    });
    payload.data.cb && payload.data.cb();
    yield put(actions.deactivateMfaRequestSuccess(res));
  } catch (error) {
    yield put(actions.deactivateMfaRequestFailure(error));
  }
}

export function* forgotPasswordRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/pub/forgot',
      method: 'POST',
      data: payload.data,
    });
    yield put(actions.forgotPasswordRequestSuccess(res));
  } catch (error) {
    yield put(actions.forgotPasswordRequestFailure(error));
  }
}

export function* getUserRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/users',
      method: 'GET',
      token: selectToken(store.getState()),
    });
    yield put(actions.getUserRequestSuccess(res));
  } catch (error) {
    yield put(actions.getUserRequestFailure(error));
  }
}

export function* loginRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/auth/login',
      method: 'POST',
      data: payload.data,
      dontCheckForUnauthorized: true,
      skipToast: true,
    });
    yield put(actions.loginRequestSuccess(res));
    AnalyticsClient.track(trackEvents.LOGIN_SUCCESS);
  } catch (error) {
    yield put(actions.loginRequestFailure(error));
    if (error?.message?.indexOf('Two-Factor authentication is required') > -1) {
      payload.data.cb && payload.data.cb();
    } else {
      AnalyticsClient.track(trackEvents.LOGIN_ERROR);
    }
  }
}

export function* logoutRequestSaga(payload) {
  try {
    yield send({
      url: '/users/logout',
      method: 'POST',
      token: payload.data?.token,
      dontCheckForUnauthorized: payload.data?.dontCheckForUnauthorized || true,
    });
    AnalyticsClient.track(trackEvents.LOGOUT);
  } catch (error) {
    console.log(error);
  }
}

export function* protectionPwdRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/auth/validate-register-password',
      params: { password: payload.data },
    });
    if (res.success) {
      yield put(actions.protectionPwdRequestSuccess(payload.data));
    } else {
      yield put(actions.protectionPwdRequestFailure('Invalid password.'));
    }
  } catch (error) {
    yield put(actions.protectionPwdRequestFailure(error));
  }
}

export function* registerRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/pub/signup',
      method: 'POST',
      data: payload.data,
    });
    yield put(actions.registerRequestSuccess(res));
    yield put(actions.registerSetSubmitted(true));
  } catch (error) {
    yield put(actions.registerSetSubmitted(false));
    yield put(actions.registerRequestFailure(error));
  }
}

export function* registerResendEmailRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/pub/signup/resend-email',
      method: 'POST',
      data: payload.data,
    });
    yield put(actions.registerResendEmailRequestSuccess(res));
  } catch (error) {
    yield put(actions.registerResendEmailRequestFailure(error));
  }
}

export function* registerEmployeeRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/pub/employee/signup',
      method: 'POST',
      data: payload.data,
    });
    yield put(actions.registerEmployeeRequestSuccess(res));
  } catch (error) {
    yield put(actions.registerEmployeeRequestFailure(error));
  }
}

export function* resetPasswordRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/pub/reset/${payload.data.resetToken}`,
      method: 'POST',
      data: payload.data.data,
    });
    yield put(actions.resetPasswordRequestSuccess(res));
    AnalyticsClient.track(trackEvents.PASSWORD_RESET);
  } catch (error) {
    yield put(actions.resetPasswordRequestFailure(error));
  }
}

export function* sendFallbackMfaCodeRequestSaga(payload) {
  try {
    yield send({
      url: `/auth/mfa-fallback`,
      method: 'POST',
      data: payload.data,
    });
    yield put(actions.sendFallbackMfaCodeRequestSuccess());
  } catch (error) {
    yield put(actions.sendFallbackMfaCodeRequestFailure(error));
  }
}

export function* updatePasswordRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/users/change-password`,
      method: 'POST',
      data: payload.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updatePasswordRequestSuccess(res));
    toastr.success('Changes saved!');
  } catch (error) {
    yield put(actions.updatePasswordRequestFailure(error));
  }
}

export function* updateUserLastActiveTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/users/timesheet/active`,
      method: 'POST',
      token: selectToken(store.getState()),
    });
    yield put(actions.updateUserLastActiveTimesheetRequestSuccess(res));
  } catch (error) {
    yield put(actions.updateUserLastActiveTimesheetRequestFailure(error));
  }
}

export function* updateUserRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/users/${payload.data.id}`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    if (payload.data.id === store.getState().user.data.id) {
      yield put(actions.updateUserRequestSuccess(res));
    }
    payload.data.cb && payload.data.cb();
  } catch (error) {
    yield put(actions.updateUserRequestFailure(error));
  }
}

export function* validateResetTokenRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/pub/reset/${payload.data.resetToken}`,
      method: 'GET',
    });
    yield put(actions.validateResetTokenRequestSuccess(res));
  } catch (error) {
    yield put(actions.validateResetTokenRequestFailure(error));
  }
}

export function* verifyMfaRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/users/verify2fa',
      method: 'POST',
      token: selectToken(store.getState()),
      data: payload.data.data,
    });
    payload.data.cb && payload.data.cb();
    yield put(actions.verifyMfaRequestSuccess(res));
  } catch (error) {
    yield put(actions.verifyMfaRequestFailure(error));
  }
}

export function* verifyUserRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/pub/verify?confirmationToken=${payload.data.confirmationToken}`,
      method: 'POST',
      data: payload.data,
    });
    yield put(actions.verifyUserRequestSuccess(res));
  } catch (error) {
    yield put(actions.verifyUserRequestFailure(error));
  }
}
